"use client";

import * as React from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  Chip,
  IconButton,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useI18n } from "i18n/I18nProvider";
import JsonLd from "components/seo/JsonLd";

type Props = {
  locale: string;
  notifyEmail?: string; // optional display
};

export default function ContactClient({ locale, notifyEmail }: Props) {
  const { t } = useI18n();

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");

  const [loading, setLoading] = React.useState(false);
  const [ok, setOk] = React.useState<string | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  const canSend = name.trim() && email.trim() && message.trim();
  const API_BASE =
    process.env.REACT_APP_API_BASE || "https://www.ritasproperties.com";

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: "Ritas Properties",
    url: "https://www.ritasproperties.com",
    areaServed: "Halkidiki, Greece",
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "ritadaldou@gmail.com",
        availableLanguage: ["el", "en"],
      },
    ],
  };
  const copy = async (val?: string) => {
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      setOk(t("contact_copied") || "Copied.");
      setTimeout(() => setOk(null), 1200);
    } catch { }
  };

  const onSubmit = async () => {
    setErr(null);
    setOk(null);

    if (!canSend) {
      setErr(t("contact_required") || "Please fill in required fields.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          subject,
          message,
          locale,
          save_to_db: true,
        }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }

      setOk(t("contact_success") || "Message sent successfully.");
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setMessage("");
    } catch (e: any) {
      setErr(t("contact_error") || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: { xs: 4, md: 7 } }}>
      <JsonLd data={orgSchema} />
      <Container maxWidth="lg">
        <Stack spacing={3}>
          {/* Hero */}
          <Box>
            <Typography
              variant="h3"
              sx={{ fontWeight: 950, letterSpacing: -1 }}
            >
              {t("contact_title") || "Contact"}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mt: 1, maxWidth: 720 }}
            >
              {t("contact_page_subtitle") ||
                "We reply fast. Send us your message for availability, sales or any question."}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                variant="outlined"
                label={t("contact_chip_fast") || "Fast reply"}
              />
              <Chip
                variant="outlined"
                label={t("contact_chip_local") || "Local expertise"}
              />
              <Chip
                variant="outlined"
                label={t("contact_chip_premium") || "Premium service"}
              />
            </Stack>
          </Box>

          <Divider />

          {/* Grid-like layout without Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
              gap: 2,
              alignItems: "start",
            }}
          >
            {/* Form card */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(800px 240px at 20% 0%, rgba(0,0,0,0.06), transparent 55%)",
                  pointerEvents: "none",
                }}
              />
              <Stack spacing={1.5} sx={{ position: "relative" }}>
                {ok ? <Alert severity="success">{ok}</Alert> : null}
                {err ? <Alert severity="error">{err}</Alert> : null}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label={t("contact_name") || "Full name"}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t("contact_email") || "Email"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label={t("contact_phone") || "Phone (optional)"}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t("contact_subject") || "Subject (optional)"}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <TextField
                  label={t("contact_message") || "Message"}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  fullWidth
                  multiline
                  minRows={6}
                />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ pt: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("contact_privacy_hint") ||
                      "By sending, you agree we may contact you back."}
                  </Typography>
                  <Button
                    onClick={onSubmit}
                    disabled={!canSend || loading}
                    variant="contained"
                    sx={{ borderRadius: 999, px: 3.2, fontWeight: 900 }}
                  >
                    {loading
                      ? t("sending") || "Sending..."
                      : t("contact_send") || "Send"}
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* Info card */}
            <Paper
              variant="outlined"
              sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}
            >
              <Stack spacing={1.2}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {t("contact_info_title") || "Contact info"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("contact_info_hint") ||
                    "Call, email or send a message. We respond quickly."}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIphoneOutlinedIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {t("contact_phone_label") || "Phone"}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    +30 6971 753 500
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1.2 }}
                  >
                    <EmailOutlinedIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {t("contact_email_label") || "Email"}
                    </Typography>
                    {notifyEmail ? (
                      <IconButton
                        size="small"
                        onClick={() => copy(notifyEmail)}
                        aria-label="Copy email"
                      >
                        <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {notifyEmail || "ritadaldou@gmail.com"}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1.2 }}
                  >
                    <LocationOnOutlinedIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {t("contact_location_label") || "Area"}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Halkidiki, Greece
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    height: 300,
                    mt: 2,
                  }}
                >
                  <iframe
                    title="Ritas Properties location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps?q=Halkidiki%20Greece&output=embed"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  {t("contact_hours_hint") || "Hours: 09:00–21:00 (daily)"}
                </Typography>
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
